import {
  APP_ENV,
  CAT_NAV_PRODUCT_RESULTS_ENV,
  CONNECTED_DISPLAY_ENV,
  DYNAMIC_COMPONENT_ENV,
  HOMEPAGE_ENV,
  PAGE_TEMPLATES,
  PAGE_TYPES,
  PERSONALIZED_HOMEPAGE_ENV,
  PRODUCT_PAGE_ENV,
  RESPONSIVE_CONTENT_ENV,
  SPACE_ENV,
  STATIC_CONTENT_ENV,
  renderingEnvironmentsForPage,
  responsiveContentOptions1,
  responsiveContentOptions2
} from './const';

/**
 * returns pageTemplateName
 * @param {string} pageTemplateName1 - first half of responsive content page template name
 * @param {string} pageTemplateName2 - second half of responsive content page template name
 * @returns {string} returns pageTemplateName
 */
export const generatePageTemplateName = (pageTemplateName1, pageTemplateName2) =>
  pageTemplateName1 + (pageTemplateName2 ? ` - ${pageTemplateName2}` : '');

/**
 * generates page Template Name caption for UI
 * @param {string} pageTemplateName - pageTemplateName
 * @returns {string} page Template Name caption for UI
 */
const generatePageTemplateNameCaption = pageTemplateName => {
  let pageTemplateNameCaption = '';
  responsiveContentOptions1.some(option1 =>
    responsiveContentOptions2.some(option2 => {
      if (generatePageTemplateName(option1.value, option2.value) === pageTemplateName) {
        pageTemplateNameCaption = `${option1.name} & ${option2.name}`;
        return true;
      }
      return false;
    })
  );
  return pageTemplateNameCaption;
};

/**
 * generates rendering environment caption for page for UI
 * @param {string} pageType - pageType
 * @param {string} pageTemplateName - pageTemplateName
 * @returns {object} object containing page Type and page tmplate name captions for UI
 */
export const generateRenderingEnvironmentCaptions = (pageType, pageTemplateName) => {
  switch (pageType) {
    case PAGE_TYPES.RESPONSIVE_CONTENT:
      return {
        pageType,
        pageTemplateName: generatePageTemplateNameCaption(pageTemplateName)
      };
    case PAGE_TYPES.DEFAULT:
      if (pageTemplateName === PAGE_TEMPLATES.CAT_NAV_PRODUCT_RESULTS) {
        return {
          pageType: CAT_NAV_PRODUCT_RESULTS_ENV.name
        };
      }
      return {
        pageType,
        pageTemplateName: generatePageTemplateNameCaption(pageTemplateName)
      };
    case PAGE_TYPES.DYNAMIC:
      return {
        pageType,
        pageTemplateName: undefined
      };
    default: {
      const pageEnv = renderingEnvironmentsForPage.filter(
        env =>
          env.value.pageType === pageType && env.value.pageTemplateName === pageTemplateName
      );
      if (pageEnv.length === 1) {
        return {
          pageType: pageEnv[0].name,
          pageTemplateName: undefined
        };
      }
      return {};
    }
  }
};

/**
 * return rendering environment based on page type and template name
 * @param {string} pageType - page type
 * @param {string} pageTemplateName - page template name
 * @returns {object} rendering environment info
 */
export const getRenderingEnvironment = (pageType, pageTemplateName) => {
  switch (pageType) {
    case PAGE_TYPES.APP:
      return {
        pageType,
        pageTemplateName: generatePageTemplateNameCaption(pageTemplateName),
        renderingEnvName: APP_ENV.name,
        supportedComponents: APP_ENV.supportedComponents
      };
    case PAGE_TYPES.CONNECTED_DISPLAY:
      return {
        pageType,
        pageTemplateName: generatePageTemplateNameCaption(pageTemplateName),
        renderingEnvName: CONNECTED_DISPLAY_ENV.name,
        supportedComponents: CONNECTED_DISPLAY_ENV.supportedComponents
      };

    // only used by CAT_NAV_PRODUCT_RESULTS_ENV
    case PAGE_TYPES.DEFAULT:
      return {
        // NOTE this is not the payload value
        pageType: CAT_NAV_PRODUCT_RESULTS_ENV.name,

        // TODO figure out why no pageTemplateName for some
        renderingEnvName: CAT_NAV_PRODUCT_RESULTS_ENV.name,
        supportedComponents: CAT_NAV_PRODUCT_RESULTS_ENV.supportedComponents
      };
    case PAGE_TYPES.DYNAMIC:
      return {
        pageType,
        renderingEnvName: DYNAMIC_COMPONENT_ENV.name,
        supportedComponents: DYNAMIC_COMPONENT_ENV.supportedComponents
      };
    case PAGE_TYPES.HOMEPAGE:
      return {
        pageType,
        renderingEnvName: HOMEPAGE_ENV.name,
        supportedComponents: HOMEPAGE_ENV.supportedComponents
      };
    case PAGE_TYPES.PDP:
      return {
        pageType,
        renderingEnvName: PRODUCT_PAGE_ENV.name,
        supportedComponents: PRODUCT_PAGE_ENV.supportedComponents
      };
    case PAGE_TYPES.PERSONALIZED_HOMEPAGE:
      return {
        pageType,
        renderingEnvName: PERSONALIZED_HOMEPAGE_ENV.name,
        supportedComponents: PERSONALIZED_HOMEPAGE_ENV.supportedComponents
      };
    case PAGE_TYPES.RESPONSIVE_CONTENT:
      return {
        pageType,
        pageTemplateName: generatePageTemplateNameCaption(pageTemplateName),
        renderingEnvName: PAGE_TYPES.RESPONSIVE_CONTENT,
        supportedComponents: RESPONSIVE_CONTENT_ENV.supportedComponents
      };
    case PAGE_TYPES.SPACE:
      return {
        pageType,
        pageTemplateName: generatePageTemplateNameCaption(pageTemplateName),
        renderingEnvName: SPACE_ENV.name,
        supportedComponents: SPACE_ENV.supportedComponents
      };
    case PAGE_TYPES.STATIC_CONTENT:
      return {
        pageType,
        renderingEnvName: STATIC_CONTENT_ENV.name,
        supportedComponents: STATIC_CONTENT_ENV.supportedComponents
      };
    default: {
      return { pageType, pageTemplateName };
    }
  }
};
